/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { AcceptDto } from '../models/accept-dto';
import { CompleteDto } from '../models/complete-dto';
import { CreateUpdateMeetingDto } from '../models/create-update-meeting-dto';
import { DeclineDto } from '../models/decline-dto';
import { FacilitateDto } from '../models/facilitate-dto';
import { MeetingDto } from '../models/meeting-dto';
import { MeetingStatusEnum } from '../models/meeting-status-enum';
import { MeetingsResponseDto } from '../models/meetings-response-dto';
import { MeetingsStatisticsDto } from '../models/meetings-statistics-dto';

@Injectable({
  providedIn: 'root',
})
export class MeetingService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation loadMeetings
   */
  static readonly LoadMeetingsPath = '/meetings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `loadMeetings()` instead.
   *
   * This method doesn't expect any request body.
   */
  loadMeetings$Response(params?: {
    page?: number;
    size?: number;
    companyId?: string;
    status?: Array<MeetingStatusEnum>;

    /**
     * - true - only meetings where the current user is requester
     * - false - only meetings where the current user&#x27;s company is a target
     * - null - both
     */
    my?: boolean;
  }): Observable<StrictHttpResponse<MeetingsResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.LoadMeetingsPath, 'get');
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('companyId', params.companyId, {});
      rb.query('status', params.status, {});
      rb.query('my', params.my, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingsResponseDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `loadMeetings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  loadMeetings(params?: {
    page?: number;
    size?: number;
    companyId?: string;
    status?: Array<MeetingStatusEnum>;

    /**
     * - true - only meetings where the current user is requester
     * - false - only meetings where the current user&#x27;s company is a target
     * - null - both
     */
    my?: boolean;
  }): Observable<MeetingsResponseDto> {

    return this.loadMeetings$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingsResponseDto>) => r.body as MeetingsResponseDto)
    );
  }

  /**
   * Path part for operation createMeeting
   */
  static readonly CreateMeetingPath = '/meetings';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createMeeting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMeeting$Response(params?: {
    body?: CreateUpdateMeetingDto
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.CreateMeetingPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createMeeting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createMeeting(params?: {
    body?: CreateUpdateMeetingDto
  }): Observable<MeetingDto> {

    return this.createMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation loadMeeting
   */
  static readonly LoadMeetingPath = '/meetings/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `loadMeeting()` instead.
   *
   * This method doesn't expect any request body.
   */
  loadMeeting$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.LoadMeetingPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `loadMeeting$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  loadMeeting(params: {
    id: string;
  }): Observable<MeetingDto> {

    return this.loadMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation updateMeeting
   */
  static readonly UpdateMeetingPath = '/meetings/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateMeeting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMeeting$Response(params: {
    id: string;
    body?: CreateUpdateMeetingDto
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.UpdateMeetingPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateMeeting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateMeeting(params: {
    id: string;
    body?: CreateUpdateMeetingDto
  }): Observable<MeetingDto> {

    return this.updateMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation deleteMeeting
   */
  static readonly DeleteMeetingPath = '/meetings/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteMeeting()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteMeeting$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.DeleteMeetingPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteMeeting$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteMeeting(params: {
    id: string;
  }): Observable<void> {

    return this.deleteMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation facilitateMeeting
   */
  static readonly FacilitateMeetingPath = '/meetings/{id}/facilitate';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `facilitateMeeting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  facilitateMeeting$Response(params: {
    id: string;
    body?: FacilitateDto
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.FacilitateMeetingPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `facilitateMeeting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  facilitateMeeting(params: {
    id: string;
    body?: FacilitateDto
  }): Observable<MeetingDto> {

    return this.facilitateMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation acceptMeeting
   */
  static readonly AcceptMeetingPath = '/meetings/{id}/accept';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `acceptMeeting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  acceptMeeting$Response(params: {
    id: string;
    body?: AcceptDto
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.AcceptMeetingPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `acceptMeeting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  acceptMeeting(params: {
    id: string;
    body?: AcceptDto
  }): Observable<MeetingDto> {

    return this.acceptMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation declineMeeting
   */
  static readonly DeclineMeetingPath = '/meetings/{id}/decline';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `declineMeeting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  declineMeeting$Response(params: {
    id: string;
    body?: DeclineDto
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.DeclineMeetingPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `declineMeeting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  declineMeeting(params: {
    id: string;
    body?: DeclineDto
  }): Observable<MeetingDto> {

    return this.declineMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation completeMeeting
   */
  static readonly CompleteMeetingPath = '/meetings/{id}/complete';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `completeMeeting()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  completeMeeting$Response(params: {
    id: string;
    body?: CompleteDto
  }): Observable<StrictHttpResponse<MeetingDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.CompleteMeetingPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `completeMeeting$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  completeMeeting(params: {
    id: string;
    body?: CompleteDto
  }): Observable<MeetingDto> {

    return this.completeMeeting$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingDto>) => r.body as MeetingDto)
    );
  }

  /**
   * Path part for operation loadMeetingsStatistics
   */
  static readonly LoadMeetingsStatisticsPath = '/statistics';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `loadMeetingsStatistics()` instead.
   *
   * This method doesn't expect any request body.
   */
  loadMeetingsStatistics$Response(params?: {
    companyId?: string;
  }): Observable<StrictHttpResponse<MeetingsStatisticsDto>> {

    const rb = new RequestBuilder(this.rootUrl, MeetingService.LoadMeetingsStatisticsPath, 'get');
    if (params) {
      rb.query('companyId', params.companyId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<MeetingsStatisticsDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `loadMeetingsStatistics$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  loadMeetingsStatistics(params?: {
    companyId?: string;
  }): Observable<MeetingsStatisticsDto> {

    return this.loadMeetingsStatistics$Response(params).pipe(
      map((r: StrictHttpResponse<MeetingsStatisticsDto>) => r.body as MeetingsStatisticsDto)
    );
  }

}
